// The boards (docs/design/orgs.md 4.2): the switcher's list, opening "My board", and how a board is
// named and linked. Each tab shows one board (org.ts reads it from ?org=); switching a tab to another
// board loads it afresh, and ↗ opens one in a new tab for Chrome's split view.

import { COMPANY_SLUG, privateLabel, type BoardListing, type BoardOrg, type BoardsList } from "@boem/core/shared";
import { createContext, useContext } from "react";
import { call } from "./api.ts";

export const boardsApi = {
  /** OEMUAP, my board (made or not yet), boards shared with me */
  list: () => call<BoardsList>("GET", "/api/orgs"),
  /** open "My board": made now if it's the first time */
  openMine: () => call<{ slug: string; name: string }>("POST", "/api/orgs/me"),
};

/** A board's address: OEMUAP is the plain / every old link uses; another board is /?org=<slug>. Pure. */
export const boardHref = (slug: string) => (slug === COMPANY_SLUG ? "/" : `/?org=${encodeURIComponent(slug)}`);

/** The two letters on a board's mark: OE for OEMUAP, the person's initial(s) otherwise. Pure. */
export function boardMark(b: Pick<BoardListing, "name" | "kind">): string {
  if (b.kind === "company") return b.name.slice(0, 2).toUpperCase();
  const first = b.name.replace(/'s board$/i, "").trim();
  return first.slice(0, 2).toUpperCase();
}

/** The roles as people say them. */
export const ROLE_WORDS = { owner: "owner", editor: "can edit", viewer: "can view" } as const;

/** A switcher row's second line. Pure. */
export function boardMeta(b: BoardListing): string {
  const count = `${b.items} item${b.items === 1 ? "" : "s"}`;
  if (b.kind === "company") return `Company board · ${count}`;
  if (!b.exists) return "Private · yours · opening it makes it";
  if (b.frozen) return `Read-only: its owner left · ${count}`;
  if (b.role !== "owner") return `${b.invitedBy ? `${b.invitedBy} invited you` : "Shared with you"} · ${ROLE_WORDS[b.role ?? "viewer"]}`;
  return `${privateLabel(b.people)} · ${count}`;
}

/** The sidebar's short line under a board's name. Pure. */
export const boardSub = (b: BoardListing) => (b.kind === "company" ? "company board" : b.role === "owner" ? (b.frozen ? "read-only" : "private") : "shared with you");

/** What this tab knows about boards: the one it shows, and the switcher's list (once loaded). */
export interface Boards {
  org: BoardOrg;
  list: BoardsList | null;
  /** load the list again (the switcher opens with it fresh) */
  reload: () => void;
  /** the signed-in person */
  viewerId?: string;
  /** open the People screen (a personal board) */
  openPeople?: () => void;
  /** open the board's settings (OEMUAP admins: Settings → GitHub) */
  openSettings?: () => void;
  /** the board this tab already shows, picked again: its main board, in place (no reload) */
  openMain?: () => void;
}

export const BoardsContext = createContext<Boards | null>(null);
export const useBoards = () => useContext(BoardsContext);

/** The listing for the board this tab shows, when the list has it. Pure. */
export function currentListing(list: BoardsList | null, slug: string): BoardListing | null {
  return [...(list?.boards ?? []), ...(list?.shared ?? [])].find((b) => b.slug === slug) ?? null;
}

// A board someone shared with you shows a dot in the switcher until you open it (orgs.md 5: the dot is
// the whole notice; no Teams message). Remembered in this browser, like the other per-person settings.
const SEEN = "buyoem.seenBoards";

/** The shared boards this browser has opened. */
export function seenBoards(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

/** Remember that this board was opened here. */
export function markSeen(slug: string) {
  try {
    const s = seenBoards();
    if (s.has(slug)) return;
    s.add(slug);
    localStorage.setItem(SEEN, JSON.stringify([...s].slice(-200)));
  } catch {
    /* a convenience only */
  }
}

/** Shared boards not opened yet: they get the dot. Pure. */
export const unseen = (list: BoardsList | null, seen: Set<string>) => (list?.shared ?? []).filter((b) => !seen.has(b.slug)).map((b) => b.slug);

/** The tab's title: "Gautam's board · Board", "OEMUAP · Archive". Pure. */
export const tabTitle = (org: Pick<BoardOrg, "name">, view: string) => `${org.name} · ${view}`;

/**
 * The tab's icon: a small square in the app's colour; a personal board's has a dot, so two tabs side
 * by side are told apart at a glance. Pure.
 */
export function favicon(personal: boolean): string {
  const dot = personal ? '<circle cx="25" cy="7" r="6.5" fill="#7A54B8" stroke="#fff" stroke-width="2"/>' : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect x="2" y="4" width="26" height="26" rx="6" fill="#1F5F66"/><path d="M9 11h12M9 17h12M9 23h8" stroke="#fff" stroke-width="2.6" stroke-linecap="round"/>${dot}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
